import { useQueryDecoratedSiloWithSuspense } from '../../../queries/decoratedSilo';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIsSiloReady } from 'app/hooks/useAreSilosReady';
import { goTo, PATH, siloPageAssetQueryStringKey } from 'app/hooks/useRoute';
import { Screen } from './shared';
import { updateURLSearchParams } from 'app/functions/url-search-params';
import { useSiloWeb3 } from '@silo/web3';
import { ChecksumAddress, RUNTIME_PROTOCOL_INSTANCES, getChecksumAddressOrThrow } from '@silo/types';
import { useQuery } from '@tanstack/react-query';
import { isValidSilo } from '@silo/contract-utils';

/**
 * @deprecated use `useThisSilo` instead. This hook doesn't make requests to the graph
 */
export const useThisDecoratedSilo = () => {
  const siloAddress = useThisSiloAddress();
  const decoratedSilo = useQueryDecoratedSiloWithSuspense(siloAddress);

  return decoratedSilo;
};

export const useThisSiloAddress = () => {
  const address = useRouterQueryAddress();

  const { data } = useIsSiloReady(address);

  return data?.isReady ? data.siloAddress : null;
};

export const useValidateSiloAddress = () => {
  const { protocolInstance, setProtocolInstance, providerPriority } = useSiloWeb3();
  const shouldSyncProtocolInstance = useRef(true);
  const router = useRouter();
  const address = useRouterQueryAddress();

  // if no address, redirect to markets
  useEffect(() => {
    if (!address && router.isReady) goTo(PATH.HOME);
  }, [router.isReady, address]);

  const { data, isSuccess } = useQuery({
    queryKey: ['isValidSilo', providerPriority, address],
    queryFn: () =>
      isValidSilo({
        address: address!,
        protocolInstances: RUNTIME_PROTOCOL_INSTANCES,
        providerPriority: providerPriority!,
      }),
    enabled: !!address && !!providerPriority,
  });

  const isInvalid = isSuccess && !data.valid;

  // if address is not a valid silo, redirect to markets
  useEffect(() => {
    if (isInvalid) {
      goTo(PATH.HOME);
    }
  }, [isInvalid]);

  useEffect(() => {
    if (address) {
      shouldSyncProtocolInstance.current = true;
    }
  }, [address]);

  const siloProtocolInstance = data?.protocolInstance;

  /**
   * If silo is on a different protocol instance, attempt to switch
   */
  useEffect(() => {
    if (!protocolInstance || !siloProtocolInstance) {
      // noop - wait until these have been determined to make an action
      return;
    }

    if (protocolInstance === siloProtocolInstance) {
      // the silo protocol instance matches the currently selected protocol instance: noop
      shouldSyncProtocolInstance.current = false;
      return;
    }

    if (shouldSyncProtocolInstance.current) {
      setProtocolInstance(siloProtocolInstance, true);
      shouldSyncProtocolInstance.current = false;
    } else {
      goTo(PATH.HOME);
    }
  }, [protocolInstance, siloProtocolInstance, setProtocolInstance]);
};

const useRouterQueryAddress = () => {
  const router = useRouter();
  const address = useMemo<ChecksumAddress | undefined>(() => {
    const queryAddress = router.query.address;

    try {
      if (Array.isArray(queryAddress) && queryAddress.length > 0 && typeof queryAddress[0] === 'string') {
        return getChecksumAddressOrThrow(queryAddress[0]);
      }
      if (typeof queryAddress === 'string') {
        return getChecksumAddressOrThrow(queryAddress);
      }
    } catch (error) {
      return;
    }
  }, [router.query]);

  return address;
};

export const useClearAssetFromQueryString = (screen: Screen) => {
  const router = useRouter();

  return useCallback(() => {
    const newPath = updateURLSearchParams((params) => {
      params.delete(siloPageAssetQueryStringKey(screen));
      return params;
    })(router.asPath);

    router.replace(newPath, undefined, {
      shallow: true,
    });
  }, [router, screen]);
};
